import { arrayBufferToBase64 } from '@@web/shared/bytes_base64'
import { documentTitleFromUploadFileName, sanitizeUploadFileName } from '@@web/shared/upload_policy'
import { processBrowserUploadFile } from '../processUploadFile'

/** Per-file cap for chat attachments (base64 inflates ~33% on the wire). */
export const CHAT_ATTACHMENT_MAX_BYTES = 20 * 1024 * 1024
/** Max files staged on a single message. */
export const CHAT_ATTACHMENT_MAX_COUNT = 8
/** Longest edge kept when downscaling photos before sending to a vision model. */
const IMAGE_MAX_EDGE = 1568
/** Extracted document text inlined into the model turn (not persisted in the message). */
export const CHAT_ATTACHMENT_INLINE_TEXT_MAX_CHARS = 20_000

export type PreparedChatImageAttachment = {
  kind: 'image'
  /** Original (sanitized) file name. */
  name: string
  mimeType: string
  /** Base64 payload without the data: prefix. */
  base64: string
}

export type PreparedChatDocumentAttachment = {
  kind: 'document'
  name: string
  /** Human title derived from the file name (library document name). */
  title: string
  mimeType: string
  /** Extracted plain text (may be a single space when nothing was extractable). */
  text: string
  /** Base64 of the original bytes when we kept them (PDF/Word/Excel/unknown binary). */
  blob?: string
}

export type PreparedChatAttachment = PreparedChatImageAttachment | PreparedChatDocumentAttachment

export type PrepareChatAttachmentFailure = { name: string; error: string }

/** Image files (by MIME, or by extension when the browser gives us nothing). */
export function isImageAttachmentFile(file: File): boolean {
  const mime = (file.type || '').toLowerCase().trim()
  if (mime.startsWith('image/')) return true
  const ext = file.name.toLowerCase().split('.').pop() ?? ''
  return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'heic', 'heif', 'avif'].includes(ext)
}

function canvasToBase64(canvas: HTMLCanvasElement, mimeType: string, quality: number): string | null {
  const url = canvas.toDataURL(mimeType, quality)
  const comma = url.indexOf(',')
  if (comma < 0) return null
  const data = url.slice(comma + 1)
  return data.length > 0 ? data : null
}

/**
 * Downscale large photos (phone cameras produce 4000px+ images) so vision requests stay
 * well inside model limits. Returns null when the browser cannot decode the file
 * (e.g. HEIC on non-Safari), letting the caller fall back to the original bytes.
 */
async function downscaleImage(file: File): Promise<{ base64: string; mimeType: string } | null> {
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return null
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file)
  } catch {
    return null
  }
  try {
    const longest = Math.max(bitmap.width, bitmap.height)
    const scale = longest > IMAGE_MAX_EDGE ? IMAGE_MAX_EDGE / longest : 1
    const width = Math.max(1, Math.round(bitmap.width * scale))
    const height = Math.max(1, Math.round(bitmap.height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.drawImage(bitmap, 0, 0, width, height)

    // Keep PNG (screenshots, transparency) as PNG; everything else re-encodes to JPEG.
    const wantPng = (file.type || '').toLowerCase() === 'image/png'
    const mimeType = wantPng ? 'image/png' : 'image/jpeg'
    const base64 = canvasToBase64(canvas, mimeType, 0.85)
    return base64 ? { base64, mimeType } : null
  } catch {
    return null
  } finally {
    bitmap.close?.()
  }
}

async function prepareImageAttachment(
  file: File,
  name: string
): Promise<PreparedChatImageAttachment | PrepareChatAttachmentFailure> {
  const downscaled = await downscaleImage(file)
  if (downscaled) {
    return { kind: 'image', name, mimeType: downscaled.mimeType, base64: downscaled.base64 }
  }
  try {
    const bytes = await file.arrayBuffer()
    const base64 = arrayBufferToBase64(bytes)
    if (!base64) return { name, error: 'read_failed' }
    return { kind: 'image', name, mimeType: file.type || 'image/jpeg', base64 }
  } catch {
    return { name, error: 'read_failed' }
  }
}

/**
 * Turn a composer file into something we can send: photos become base64 image payloads
 * (downscaled), everything else goes through the shared upload pipeline for text extraction.
 */
export async function prepareChatAttachment(
  file: File
): Promise<PreparedChatAttachment | PrepareChatAttachmentFailure> {
  const name = sanitizeUploadFileName(file.name)
  if (file.size > CHAT_ATTACHMENT_MAX_BYTES) {
    return { name, error: 'too_large' }
  }
  if (isImageAttachmentFile(file)) {
    return prepareImageAttachment(file, name)
  }
  const parsed = await processBrowserUploadFile(file)
  if ('error' in parsed) {
    return { name: parsed.originalName, error: parsed.error }
  }
  return {
    kind: 'document',
    name: parsed.originalName,
    title: parsed.title.trim() || documentTitleFromUploadFileName(parsed.originalName),
    mimeType: parsed.mimeType,
    text: parsed.content,
    ...(parsed.blob ? { blob: parsed.blob } : {}),
  }
}

/**
 * Text block appended to the model turn (not to the stored message) so the assistant can
 * read attached documents immediately, without waiting for knowledgebase indexing.
 */
export function formatDocumentAttachmentsForModel(
  docs: readonly PreparedChatDocumentAttachment[]
): string {
  const blocks = docs
    .map((doc) => {
      const body = doc.text.trim()
      if (!body) return ''
      const clipped =
        body.length > CHAT_ATTACHMENT_INLINE_TEXT_MAX_CHARS
          ? `${body.slice(0, CHAT_ATTACHMENT_INLINE_TEXT_MAX_CHARS)}\n… (truncated; full document is in the workspace library)`
          : body
      return `<attached_document name="${doc.name}" mime="${doc.mimeType}">\n${clipped}\n</attached_document>`
    })
    .filter(Boolean)
  if (!blocks.length) return ''
  return `\n\nThe user attached the following document(s) to this message. They are also saved in the workspace library.\n\n${blocks.join('\n\n')}`
}

/** Human-readable file size for composer chips. */
export function formatAttachmentSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
